import type { EnqueueMode, QueueItem } from './types'

/**
 * Compute the index at which new tracks should be inserted for a given enqueue mode.
 * Assumes the queue is non-empty (caller must handle the empty-queue case).
 */
export function computeInsertAt(
  queue: QueueItem[],
  currentIndex: number,
  mode: EnqueueMode
): number {
  switch (mode) {
    case 'after-current':
      return currentIndex + 1
    case 'after-next':
      return currentIndex + 2
    case 'after-album': {
      const currentAlbumId = queue[currentIndex]?.albumId
      let i = currentIndex + 1
      while (i < queue.length && queue[i]?.albumId === currentAlbumId) i++
      return i
    }
    case 'after-artist': {
      const currentArtistId = queue[currentIndex]?.artistId
      let i = currentIndex + 1
      while (i < queue.length && queue[i]?.artistId === currentArtistId) i++
      return i
    }
    case 'at-end':
      return queue.length
  }
}

/** Remove a track at `removeIndex`, adjusting `currentIndex` to remain on the same track. */
export function applyRemove(
  queue: QueueItem[],
  currentIndex: number,
  removeIndex: number
): { queue: QueueItem[]; currentIndex: number } {
  const newQueue = queue.filter((_, i) => i !== removeIndex)
  let newCurrentIndex = currentIndex
  if (removeIndex < currentIndex) {
    newCurrentIndex--
  } else if (removeIndex === currentIndex) {
    newCurrentIndex = Math.min(currentIndex, newQueue.length - 1)
  }
  return { queue: newQueue, currentIndex: newCurrentIndex }
}

/** Move a track from index `from` to index `to`, keeping `currentIndex` on the same track. */
export function applyMove(
  queue: QueueItem[],
  currentIndex: number,
  from: number,
  to: number
): { queue: QueueItem[]; currentIndex: number } {
  if (from === to) return { queue, currentIndex }
  const newQueue = [...queue]
  const [moved] = newQueue.splice(from, 1)
  if (!moved) return { queue, currentIndex }
  newQueue.splice(to, 0, moved)

  let newCurrentIndex = currentIndex
  if (currentIndex === from) {
    newCurrentIndex = to
  } else if (from < currentIndex && to >= currentIndex) {
    newCurrentIndex--
  } else if (from > currentIndex && to <= currentIndex) {
    newCurrentIndex++
  }

  return { queue: newQueue, currentIndex: newCurrentIndex }
}
